import React, { useEffect, useRef } from 'react';
import Webcam from 'react-webcam';
import { BarcodeDetector } from 'barcode-detector';
import type { BarcodeFormat } from 'barcode-detector';

/**
 * Web barcode scanner that decodes 1D (incl. NW-7 / Codabar) and QR codes.
 *
 * Why this exists: the previous scanner used `react-qr-barcode-scanner`, which
 * wraps `@zxing/library@0.17.1` — a build whose Codabar decoder is disabled,
 * so NW-7 could never be read. This component uses the `barcode-detector`
 * ponyfill (ZXing-wasm) which supports Codabar across all browsers including
 * iOS Safari.
 *
 * It renders a camera preview via `react-webcam` and polls the live video
 * element with `BarcodeDetector.detect` on a fixed interval.
 */
const SCAN_FORMATS: BarcodeFormat[] = [
  'codabar',
  'qr_code',
  'code_128',
  'code_39',
  'ean_13',
  'ean_8',
  'itf',
  'upc_a',
  'upc_e',
];

const HAVE_ENOUGH_DATA = 4; // HTMLMediaElement.HAVE_ENOUGH_DATA

interface WebcamBarcodeScannerProps {
  onDetected: (value: string) => void;
  onError?: (error: unknown) => void;
  delay?: number;
}

const WebcamBarcodeScanner: React.FC<WebcamBarcodeScannerProps> = ({
  onDetected,
  onError,
  delay = 300,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const detectorRef = useRef<BarcodeDetector | null>(null);
  const detectedRef = useRef(false);

  useEffect(() => {
    detectorRef.current = new BarcodeDetector({ formats: SCAN_FORMATS });
    detectedRef.current = false;

    const interval = setInterval(async () => {
      if (detectedRef.current) {
        return;
      }
      const video = webcamRef.current?.video;
      const detector = detectorRef.current;
      if (!video || video.readyState !== HAVE_ENOUGH_DATA || !detector) {
        return;
      }
      try {
        const results = await detector.detect(video);
        const first = results?.[0];
        if (first?.rawValue && !detectedRef.current) {
          detectedRef.current = true;
          onDetected(first.rawValue);
        }
      } catch (error: unknown) {
        onError?.(error);
      }
    }, delay);

    return () => {
      detectedRef.current = true;
      clearInterval(interval);
    };
  }, [onDetected, onError, delay]);

  return (
    <Webcam
      ref={webcamRef}
      audio={false}
      screenshotFormat="image/jpeg"
      videoConstraints={{ facingMode: 'environment' }}
      onUserMediaError={(e)=>{
        console.error(e);
        onError?.(e);
      }}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

export default WebcamBarcodeScanner;
